import { useEffect, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { fetchQuery, useFragment, useLazyLoadQuery } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { SendRequestAfterSaving } from "@/components/request-upload-files-dialog"
import { RequestUploadFilesMenu } from "@/components/request-upload-files-menu"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { getAllowedAppointmentTypes, isFlatLength } from "@/lib/appointment-type-rules"
import { getServiceMapping } from "@/lib/fetch-service-mappings"
import { scrollIntoField } from "@/lib/scrollToId"
import { AddressSection } from "@/modules/patients/agenda/teleconsultation/booking/address-section"
import { AgentSkill } from "@/modules/patients/agenda/teleconsultation/booking/agent-skills/agent-skill"
import { LanguageSkill } from "@/modules/patients/agenda/teleconsultation/booking/agent-skills/language-skill"
import { CommunicationChannelSelection } from "@/modules/patients/agenda/teleconsultation/booking/components/communication-channel-selection"
import { CommunicationRecipientSelection } from "@/modules/patients/agenda/teleconsultation/booking/components/communication-recipient-selection"
import {
  formatPartnerAddress,
  MedgatePartnerSelect,
} from "@/modules/patients/agenda/teleconsultation/booking/components/medgate-partner-select"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { DateSlotSelector } from "@/modules/patients/agenda/teleconsultation/booking/date-slot-selector/date-slot-selector"
import { useAddressManager } from "@/modules/patients/agenda/teleconsultation/booking/hooks/useAddressManager"
import {
  InboundCallContext,
  useDefaultValues,
} from "@/modules/patients/agenda/teleconsultation/booking/hooks/useDefaultValues"
import { useFollowUpProgressNotes } from "@/modules/patients/agenda/teleconsultation/booking/hooks/useFollowUpProgressNotes"
import { isChatSelectable } from "@/modules/patients/agenda/teleconsultation/booking/libs/teleconsultation-helper"
import { PreventionProgramsSection } from "@/modules/patients/agenda/teleconsultation/booking/prevention-programs-section"
import { ReasonForEncounterSection } from "@/modules/patients/agenda/teleconsultation/booking/reason-for-encounter-section"
import { ServiceSpecialtySection } from "@/modules/patients/agenda/teleconsultation/booking/service-specialty-section"
import {
  chFavoriteAgentsByPatientQuery,
  chFavoriteAgentsByPatientQuery$data,
} from "@/modules/patients/agenda/teleconsultation/booking/services/__generated__/chFavoriteAgentsByPatientQuery.graphql"
import { chPartnerUsersQuery } from "@/modules/patients/agenda/teleconsultation/booking/services/__generated__/chPartnerUsersQuery.graphql"
import {
  deFavoriteAgentsByPatientQuery,
  deFavoriteAgentsByPatientQuery$data,
} from "@/modules/patients/agenda/teleconsultation/booking/services/__generated__/deFavoriteAgentsByPatientQuery.graphql"
import { ch_FavoriteAgentsByPatientQuery } from "@/modules/patients/agenda/teleconsultation/booking/services/chFavoriteAgentsByPatientQuery"
import { ch_PartnerUsersQuery } from "@/modules/patients/agenda/teleconsultation/booking/services/chPartnerUsersQuery"
import { de_FavoriteAgentsByPatientQuery } from "@/modules/patients/agenda/teleconsultation/booking/services/deFavoriteAgentsByPatientQuery"
import { SubmitButton } from "@/modules/patients/agenda/teleconsultation/booking/submit-btn"
import {
  PartnerUser,
  TeleconsultationForm,
  TeleconsultationFormInput,
  teleconsultationSchema,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { UrgencySection } from "@/modules/patients/agenda/teleconsultation/booking/urgency/urgency-section"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useRequestFileUpload } from "@/modules/patients/hooks/useRequestFileUpload"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { Appointment } from "@/modules/patients/services/PatientAppointment"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { usePatientAgenda } from "@/modules/patients/services/usePatientAgenda"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { RelayEnvironment } from "@/relay-environment"
import { useDrawerStore } from "@/stores/useDrawerStore"
import {
  Country,
  FileUploadRequestType,
  MedgateConnectChannel,
  Service,
  Urgency,
} from "@/types/__generated__/union-types"

export const TELECONSULTATION_DRAWER_OPTIONS = {
  ADD_APPOINTMENT: "ADD_APPOINTMENT",
  FOLLOWUP_FOR_PROGRESS_NOTE: "FOLLOWUP_FOR_PROGRESS_NOTE",
  EDITING_APPOINTMENT: "EDITING_APPOINTMENT",
} as const

export const OPEN_TELECONSULTATION_PARAM = "openTeleconsultation"

export const OPEN_TELECONSULTATION_VALUE = { ADD_APPOINTMENT: "add-appointment" } as const

export type OpenTeleconsultationValue = (typeof OPEN_TELECONSULTATION_VALUE)[keyof typeof OPEN_TELECONSULTATION_VALUE]

export type TeleconsultationMode =
  | {
      kind: typeof TELECONSULTATION_DRAWER_OPTIONS.ADD_APPOINTMENT
      data: InboundCallContext & {
        defaultReason?: string | null
        defaultService?: Service
        defaultPreventionProgramKey?: string | null
      }
    }
  | {
      kind: typeof TELECONSULTATION_DRAWER_OPTIONS.FOLLOWUP_FOR_PROGRESS_NOTE
      data: InboundCallContext & { progressNote: ProgressNoteData }
    }
  | {
      kind: typeof TELECONSULTATION_DRAWER_OPTIONS.EDITING_APPOINTMENT
      data: { appointment: Appointment }
    }

const handleFetch = (
  country: Country,
  input: (chFavoriteAgentsByPatientQuery | deFavoriteAgentsByPatientQuery)["variables"],
  next: (data: chFavoriteAgentsByPatientQuery$data | deFavoriteAgentsByPatientQuery$data) => void,
) => {
  const query = country === "CH" ? ch_FavoriteAgentsByPatientQuery : de_FavoriteAgentsByPatientQuery
  fetchQuery<chFavoriteAgentsByPatientQuery | deFavoriteAgentsByPatientQuery>(RelayEnvironment, query, input, {
    fetchPolicy: "network-only",
  }).subscribe({ next })
}

type TeleconsultationDrawerProps = {
  mode: TeleconsultationMode
  countryCode: Country
  patientId: string
  appointmentId: string | null
  onClose?: () => void
}

export const TeleconsultationDrawer = (props: TeleconsultationDrawerProps) => {
  const { appointmentId, countryCode, mode: incomingMode, patientId, onClose } = props
  const { t, i18n } = useTranslation()
  const { role } = useUser()
  const closeDrawer = useDrawerStore((store) => store.closeDrawer)

  const editingAppointment = incomingMode.kind === "EDITING_APPOINTMENT" ? incomingMode.data.appointment : null
  const isNormalAppointment = editingAppointment?.service === "CONSULTATION" && editingAppointment?.specialty === "NONE"
  const isRestrictedByAppBooking =
    editingAppointment?.access === "APP" && role !== "MedicalSupervisor" && !isNormalAppointment

  const [patientRef] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const partnerData = useLazyLoadQuery<chPartnerUsersQuery>(ch_PartnerUsersQuery, {
    skip: countryCode !== "CH",
  })
  const countryPatient = patientRef.patientById

  if (!countryPatient) {
    throw new Error("Not Found The Patient")
  }

  const { address, contactChannel } = countryPatient
  const partners = (partnerData.ch_partnerUsers?.nodes ?? []).filter((p) => p.isActive)

  const patient = useFragment<PatientFragment$key>(PatientFragment, countryPatient)
  const [favoriteAgents, setFavoriteAgents] = useState<
    chFavoriteAgentsByPatientQuery$data | deFavoriteAgentsByPatientQuery$data | null
  >(null)

  const { secondaryLanguages, phoneNumber } = patient
  const { city, country: countryObject, houseNumberStreet, postalCode } = address || {}

  const { mode, callerPhoneNumber, defaultValues } = useDefaultValues(incomingMode, countryPatient, partners)
  const [sendRequestAfterSaving, setSendRequestAfterSaving] = useState<SendRequestAfterSaving>({
    isSendRequestAfterSaving: false,
  })
  const { agendaData } = usePatientAgenda(countryCode, patientId, i18n.language)

  const selectedAppointment = agendaData?.appointments?.edges?.find(({ node }) => node.id === defaultValues.id)?.node
  const { handleSendRequest, handleCancelRequest, ...fileUploadRequestData } = useRequestFileUpload({
    appointmentId: selectedAppointment?.id ?? "",
    fileUploadRequestRef: mode.kind === "EDITING_APPOINTMENT" ? selectedAppointment?.fileUploadRequest : null,
  })

  const form = useForm<TeleconsultationFormInput, unknown, TeleconsultationForm>({
    mode: "all",
    resolver: zodResolver(teleconsultationSchema(contactChannel)),
    defaultValues,
  })

  const [isAutoSwitchedToKidsLine, setIsAutoSwitchedToKidsLine] = useState(false)

  const {
    formState: { errors, dirtyFields },
    control,
    setValue,
    getValues,
    clearErrors,
  } = form

  const { processAddressChange } = useAddressManager(setValue, getValues, clearErrors)

  const serviceWatch = useWatch({ control, name: "service" })
  const appointmentTypeWatch = useWatch({ control, name: "appointmentType" })
  const followUpIdWatch = useWatch({ control, name: "followUpForProgressNoteId" })
  const assignedPartnerWatch = useWatch({ control, name: "assignedPartner" })
  const followUpProgressNotes = useFollowUpProgressNotes(getValues("followUpForProgressNoteId"))

  const partnerInfo = assignedPartnerWatch ? (partners.find((p) => p.email === assignedPartnerWatch) ?? null) : null
  const isMedgateConnectService = isMedgateConnect(serviceWatch)
  const showWaitingForDocuments =
    appointmentTypeWatch === "FOLLOW_UP" &&
    (mode.kind !== "EDITING_APPOINTMENT" || mode.data.appointment.medgateConnectOrder?.status === "OPEN")

  useDirtyDrawerMaker(Object.keys(dirtyFields).length > 0)

  const handleServiceChange = (service: Service) => {
    const isChatSelectableForService = isChatSelectable({
      contactChannel,
      service,
      isMedicalChatEnabled: countryPatient.medgateServiceInfo.isMedicalChatEnabled,
    })
    if (mode.kind !== "EDITING_APPOINTMENT" && getValues("channel") === "CHAT" && !isChatSelectableForService) {
      setValue("channel", "PHONE")
    }

    if (isMedgateConnect(service)) {
      setValue("appointmentType", followUpProgressNotes.length > 0 ? "FOLLOW_UP" : "LONGER")
      setValue("urgency", "NORMAL")
      setValue("flexibility", "FIXED_TIME")
      setValue("requestType", "NONE")

      if (mode.kind === "FOLLOWUP_FOR_PROGRESS_NOTE") {
        const { channel } = mode.data.progressNote.appointment
        setValue("channel", channel === "VIDEO" || channel === "PHONE" ? channel : "VIDEO")
      }
    } else {
      setValue("assignedPartner", null)
      setValue("partnerType", null)
      setValue("isPartnerDoc2U", null)
      processAddressChange({ type: "leave-medgate-connect", fallbackPhone: callerPhoneNumber || phoneNumber })

      if (service === "TELE_GENERAL_PRACTITIONER") {
        setValue("channel", "VIDEO")
      } else if (service === "ADMINISTRATIVE_REQUEST") {
        setValue("appointmentType", "NORMAL")
      } else if (service === "STUDY_CALL") {
        setValue("appointmentType", "LONGER")
      }

      const allowedAppointmentTypes = getAllowedAppointmentTypes(service)
      const currentAppointmentType = getValues("appointmentType")
      if (!currentAppointmentType || !allowedAppointmentTypes.includes(currentAppointmentType)) {
        setValue("appointmentType", allowedAppointmentTypes[0])
      }

      // The screen hides the controls a flat-length appointment fixes, so their values are set here instead
      if (isFlatLength(getValues("appointmentType"))) {
        setValue("urgency", "NORMAL")
        setValue("flexibility", "FIXED_TIME")
        setValue("requestType", "NONE")
        if (getValues("channel") === "CHAT") {
          setValue("channel", "PHONE")
        }
      }
    }
  }

  const handleUrgencyChange = (urgency: Urgency) => {
    const isUrgentOrAcute = urgency === "MEDICAL_URGENT" || urgency === "MEDICAL_ACUTE"
    const isKidsLineAllowedInCountry = getServiceMapping().serviceMappings.some(
      (mapping) => mapping.service === "KIDS_LINE" && mapping.isAllowedInCountries.includes(countryCode),
    )
    if (
      isKidsLineAllowedInCountry &&
      isUrgentOrAcute &&
      patient.age.years < 18 &&
      getValues("service") !== "KIDS_LINE"
    ) {
      setValue("service", "KIDS_LINE")
      setValue("specialty", "NONE")
      handleServiceChange("KIDS_LINE")
      setIsAutoSwitchedToKidsLine(true)
    } else setIsAutoSwitchedToKidsLine(false)
  }

  const handlePartnerChange = (partner: PartnerUser | null) => {
    setValue("partnerType", partner?.userTag ?? null)
    setValue("isPartnerDoc2U", partner?.isDoc2U ?? null)
    if (!partner) {
      processAddressChange({ type: "partner-cleared" })
      return
    }
    processAddressChange({ type: "partner-selected", partnerPhone: partner.phoneNumber })
  }

  const handleRecipientChange = (value: MedgateConnectChannel) => {
    if (value === "PARTNER") {
      processAddressChange({ type: "enter-partner", partnerPhone: partnerInfo?.phoneNumber ?? null })
    } else {
      processAddressChange({ type: "leave-partner", fallbackPhone: callerPhoneNumber || phoneNumber })
    }
  }

  useEffect(() => {
    if (!favoriteAgents) handleFetch(countryCode, { patientId }, setFavoriteAgents)
  }, [countryCode, favoriteAgents, patientId])

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      scrollIntoField(Object.keys(errors)[0], "end")
    }
  }, [errors])

  const handleFileUploadRequest = (selectedType?: FileUploadRequestType, onSendRequestCompleted?: () => void) => {
    const { id: appointmentId } = defaultValues

    switch (mode.kind) {
      case "ADD_APPOINTMENT":
      case "FOLLOWUP_FOR_PROGRESS_NOTE":
        setSendRequestAfterSaving({
          isSendRequestAfterSaving: true,
          documentType: selectedType,
        })
        onSendRequestCompleted?.()
        break
      case "EDITING_APPOINTMENT":
        if (appointmentId) {
          handleSendRequest(selectedType, onSendRequestCompleted)
        }
        break
    }
  }

  const handleCloseDrawer = () => {
    closeDrawer()
    onClose?.()
  }

  return (
    <Drawer defaultOpen={false} open onOpenChange={handleCloseDrawer}>
      <DrawerContent
        className="shadow-1 @xxl:absolute @xxl:right-0 z-50 min-w-260! border"
        data-testid="teleconsultation-drawer">
        <DrawerClose className="top-3 right-2" />
        <DrawerHeader className="h-16 justify-center pt-4 pr-4 pb-2 pl-6">
          <DrawerTitle
            className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium"
            data-testid="teleconsultation-drawer-title">
            {t("patient.agenda.teleconsultation")}
          </DrawerTitle>
        </DrawerHeader>
        <FormProvider {...form}>
          <DrawerBody className="wrapper-container group/scrollbar grid grid-rows-1 overflow-hidden p-0">
            <div className="grid h-full grid-cols-2 gap-4 px-4">
              <ScrollWrapper className="animate-fade-in-bottom scrollbar-thumb-surface-pressed h-full scrollbar-track-transparent p-0 px-px pb-1 transition-all duration-300">
                <div className="flex flex-col gap-8 p-1 py-4">
                  <section className="flex flex-col gap-4">
                    <ServiceSpecialtySection
                      mode={mode}
                      isRestrictedByAppBooking={isRestrictedByAppBooking}
                      onServiceChange={(service) => {
                        setIsAutoSwitchedToKidsLine(false)
                        handleServiceChange(service)
                      }}
                    />
                    {isMedgateConnectService && (
                      <MedgatePartnerSelect
                        key={followUpIdWatch}
                        partners={partners}
                        onPartnerChange={handlePartnerChange}
                      />
                    )}
                    <PreventionProgramsSection />
                    <UrgencySection
                      mode={mode}
                      countryPatient={countryPatient}
                      followUpProgressNotes={followUpProgressNotes}
                      partners={partners}
                      onUrgencyChange={handleUrgencyChange}
                      isAutoSwitchedToKidsLine={isAutoSwitchedToKidsLine}
                    />
                    <div data-testid="agent-skills-title" className="text-on-surface mt-4 font-medium">
                      {t("patient.agenda.agentSkills.title")}
                    </div>
                    <LanguageSkill secondaryLanguages={[...secondaryLanguages]} />
                    {favoriteAgents && (
                      <AgentSkill mode={mode} favoriteAgents={favoriteAgents.patientById?.favoriteAgentUserNames} />
                    )}
                  </section>

                  <section className="flex flex-col gap-4">
                    <div className="text-on-surface text-base font-medium">
                      {t("patient.agenda.appointmentInformation")}
                    </div>

                    <ReasonForEncounterSection
                      patientFragmentRef={countryPatient}
                      isRestrictedByAppBooking={isRestrictedByAppBooking}
                    />

                    {isMedgateConnectService && (
                      <CommunicationRecipientSelection onRecipientChange={handleRecipientChange} />
                    )}
                    <CommunicationChannelSelection
                      contactChannel={countryPatient.contactChannel}
                      isEditing={mode.kind === TELECONSULTATION_DRAWER_OPTIONS.EDITING_APPOINTMENT}
                      isMedicalChatEnabled={countryPatient.medgateServiceInfo.isMedicalChatEnabled}
                    />
                    {isMedgateConnectService ? (
                      <>
                        {showWaitingForDocuments && (
                          <Banner type="inline" variant="info" data-testid="medgate-connect-waiting-for-documents">
                            {t("scheduling.waitingRoom.requestDocument.waitingForDocument")}
                          </Banner>
                        )}
                        {partnerInfo && (
                          <Banner type="inline" variant="info" data-testid="medgate-connect-doc2u-info">
                            {t(
                              partnerInfo.isDoc2U
                                ? "patient.agenda.medgateConnect.partnerIsDoc2uCapable"
                                : "patient.agenda.medgateConnect.partnerIsNotDoc2uCapable",
                            )}
                          </Banner>
                        )}
                      </>
                    ) : (
                      <>
                        <RequestUploadFilesMenu
                          onSendRequest={handleFileUploadRequest}
                          sendRequestAfterSaving={{
                            isSendRequestAfterSaving: ["ADD_APPOINTMENT", "FOLLOWUP_FOR_PROGRESS_NOTE"].includes(
                              mode.kind,
                            ),
                          }}
                          contactChannel={patient.contactChannel}
                          isContactChannelVerified={patient.isContactChannelVerified}
                          onCancelRequest={handleCancelRequest}
                          {...fileUploadRequestData}
                        />
                        {sendRequestAfterSaving.isSendRequestAfterSaving && (
                          <Banner variant="warning" type="inline">
                            {t("patient.uploadFiles.requestWillBeSent")}
                          </Banner>
                        )}
                      </>
                    )}
                    <AddressSection
                      city={city}
                      countryIsoCode2={countryObject?.code}
                      houseNumberStreet={houseNumberStreet}
                      postalCode={postalCode}
                      partnerAddress={formatPartnerAddress(partnerInfo)}
                    />
                  </section>
                </div>
              </ScrollWrapper>
              <ScrollWrapper className="animate-fade-in-bottom scrollbar-w-1 scrollbar-thumb-surface-pressed mb-10 scrollbar-track-transparent pb-5 transition-all duration-300">
                <DateSlotSelector isEditMode={mode.kind === TELECONSULTATION_DRAWER_OPTIONS.EDITING_APPOINTMENT} />
              </ScrollWrapper>
            </div>
          </DrawerBody>
        </FormProvider>
        <DrawerFooter
          className="sticky inset-x-0 bottom-0 flex justify-end gap-2"
          data-testid="teleconsultation-drawer-footer">
          {mode.kind === "EDITING_APPOINTMENT" && (
            <LastModificationInfo
              updatedAt={mode.data.appointment.updatedAt}
              updatedBy={mode.data.appointment.updatedBy}
            />
          )}
          <Button onClick={handleCloseDrawer} variant="secondary" data-testid="teleconsultation-drawer-cancel-button">
            {t("common.button.cancel")}
          </Button>
          <FormProvider {...form}>
            <SubmitButton
              mode={mode}
              handleCloseDrawer={handleCloseDrawer}
              sendRequestAfterSaving={sendRequestAfterSaving}
            />
          </FormProvider>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
